<template>
  <div class="tax-agency-page">
    <div class="container">
      <h1 class="page-title">工商税务服务</h1>

      <!-- 税务报表 - 始终显示 -->
      <el-card class="filings-card">
        <div class="section-header">
          <h2 class="section-title">企业报税信息</h2>
          <el-select v-model="selectedEnterpriseId" placeholder="选择企业查看报税信息" clearable filterable
            @change="loadTaxFilings" style="width: 300px;">
            <el-option v-for="enterprise in enterprises" :key="enterprise.id" :label="enterprise.name"
              :value="enterprise.id">
            </el-option>
          </el-select>
        </div>

        <el-table :data="displayedFilings" v-loading="filingsLoading">
          <el-table-column prop="enterpriseName" label="企业名称" min-width="200"></el-table-column>
          <el-table-column prop="filingDate" label="报税日期" width="120"></el-table-column>
          <el-table-column prop="taxType" label="税种" width="120"></el-table-column>
          <el-table-column prop="taxAmount" label="税额（元）" width="120">
            <template slot-scope="scope">
              ¥{{ scope.row.taxAmount.toLocaleString() }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="getFilingStatusType(scope.row.status)">{{ scope.row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remarks" label="备注" show-overflow-tooltip></el-table-column>
        </el-table>

        <div v-if="displayedFilings.length === 0 && !filingsLoading" class="empty-tip">
          <el-empty description="暂无报税信息"></el-empty>
        </div>
      </el-card>

      <!-- 申请表单 - 仅登录用户显示 -->
      <el-card v-if="isLoggedIn" class="form-card">
        <h2 class="section-title">税务代办申请</h2>
        <el-form :model="form" :rules="rules" ref="taxForm" label-width="120px">
          <el-form-item label="企业ID" prop="enterpriseId">
            <el-input v-model.number="form.enterpriseId" placeholder="请输入企业ID"></el-input>
          </el-form-item>

          <el-form-item label="代办事项类型" prop="applicationType">
            <el-select v-model="form.applicationType" placeholder="请选择代办事项类型" style="width: 100%">
              <el-option label="税务登记" value="TAX_REGISTRATION"></el-option>
              <el-option label="税务申报" value="TAX_DECLARATION"></el-option>
              <el-option label="税务变更" value="TAX_CHANGE"></el-option>
              <el-option label="税务注销" value="TAX_CANCELLATION"></el-option>
              <el-option label="发票申请" value="INVOICE_APPLICATION"></el-option>
              <el-option label="税务咨询" value="TAX_CONSULTATION"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="详细说明" prop="description">
            <el-input v-model="form.description" type="textarea" :rows="5" placeholder="请详细说明代办事项"></el-input>
          </el-form-item>

          <el-form-item label="附件上传">
            <el-upload :action="uploadUrl" :headers="uploadHeaders" :on-success="handleUploadSuccess"
              :on-error="handleUploadError" :file-list="fileList" :limit="3">
              <el-button size="small" type="primary">点击上传</el-button>
              <div slot="tip" class="el-upload__tip">只能上传jpg/png/pdf文件，且不超过10MB</div>
            </el-upload>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="submitForm" :loading="submitting">提交申请</el-button>
            <el-button @click="resetForm">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 登录提示 - 仅未登录用户显示 -->
      <el-card v-else class="login-prompt-card">
        <div class="login-prompt">
          <i class="el-icon-warning"></i>
          <p>请<router-link to="/login">登录</router-link>后提交税务代办申请</p>
        </div>
      </el-card>

      <!-- 我的申请列表 - 仅登录用户显示 -->
      <el-card v-if="isLoggedIn" class="applications-card">
        <h2 class="section-title">我的申请</h2>
        <el-table :data="applications" v-loading="loading">
          <el-table-column prop="applicationType" label="代办事项" width="120">
            <template slot-scope="scope">
              {{ getApplicationTypeLabel(scope.row.applicationType) }}
            </template>
          </el-table-column>
          <el-table-column prop="description" label="说明" show-overflow-tooltip></el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.status)">{{ getStatusLabel(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="submitDate" label="提交时间" width="180"></el-table-column>
        </el-table>
      </el-card>
    </div>
  </div>
</template>

<script>
import api from '../api/config'

export default {
  name: 'TaxAgency',
  data() {
    return {
      form: {
        enterpriseId: null,
        applicationType: '',
        description: '',
        attachmentUrls: ''
      },
      rules: {
        enterpriseId: [
          { required: true, message: '请输入企业ID', trigger: 'blur' }
        ],
        applicationType: [
          { required: true, message: '请选择代办事项类型', trigger: 'change' }
        ],
        description: [
          { required: true, message: '请输入详细说明', trigger: 'blur' }
        ]
      },
      fileList: [],
      applications: [],
      taxFilings: [],
      enterprises: [],
      selectedEnterpriseId: null,
      enterpriseMap: {},
      loading: false,
      filingsLoading: false,
      submitting: false
    }
  },
  computed: {
    displayedFilings() {
      return this.taxFilings.map(filing => ({
        ...filing,
        enterpriseName: this.enterpriseMap[filing.enterpriseId] || `企业ID: ${filing.enterpriseId}`
      }))
    },
    isLoggedIn() {
      return !!localStorage.getItem('token')
    },
    uploadUrl() {
      return '/api/enterprise-management/tax-applications/upload'
    },
    uploadHeaders() {
      return {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }
    }
  },
  mounted() {
    this.loadEnterprises()
    this.loadTaxFilings()
    if (this.isLoggedIn) {
      this.loadMyApplications()
    }
  },
  methods: {
    async submitForm() {
      this.$refs.taxForm.validate(async (valid) => {
        if (!valid) {
          return false
        }

        try {
          this.submitting = true

          // 准备提交数据
          const submitData = { ...this.form }

          // 将attachmentUrls转换为JSON字符串
          if (Array.isArray(submitData.attachmentUrls)) {
            submitData.attachmentUrls = JSON.stringify(submitData.attachmentUrls)
          } else if (!submitData.attachmentUrls) {
            submitData.attachmentUrls = '[]'
          }

          const response = await api.post('/enterprise-management/tax-applications', submitData)

          if (response.success) {
            this.$message.success('申请提交成功')
            this.resetForm()
            this.loadMyApplications()
          }
        } catch (error) {
          console.error('提交申请失败:', error)
          this.$message.error('提交申请失败: ' + (error.message || '未知错误'))
        } finally {
          this.submitting = false
        }
      })
    },
    resetForm() {
      this.$refs.taxForm.resetFields()
      this.fileList = []
      this.form.attachmentUrls = ''
    },
    handleUploadSuccess(response) {
      if (response.success) {
        // 将文件URL添加到attachmentUrls数组中
        if (!this.form.attachmentUrls) {
          this.form.attachmentUrls = []
        }
        if (typeof this.form.attachmentUrls === 'string') {
          this.form.attachmentUrls = this.form.attachmentUrls ? JSON.parse(this.form.attachmentUrls) : []
        }
        this.form.attachmentUrls.push(response.data)
        this.$message.success('文件上传成功')
      } else {
        console.warn('文件上传响应异常:', response)
        this.$message.warning('文件上传成功但响应异常')
      }
    },
    handleUploadError(err, file, fileList) {
      console.error('文件上传失败:', err)
      console.error('失败的文件:', file)
      console.error('当前文件列表:', fileList)
      this.$message.error('文件上传失败: ' + (err.message || '未知错误'))
    },
    async loadEnterprises() {
      try {
        const response = await api.get('/enterprises')
        this.enterprises = response.data || []

        // 创建企业ID到名称的映射
        this.enterpriseMap = {}
        this.enterprises.forEach(e => {
          this.enterpriseMap[e.id] = e.name
        })
      } catch (error) {
        console.error('加载企业列表失败:', error)
      }
    },
    async loadTaxFilings() {
      try {
        this.filingsLoading = true
        let url = '/enterprise-management/tax-filings'

        // 如果选择了企业，只加载该企业的报税信息
        if (this.selectedEnterpriseId) {
          url = `/enterprise-management/tax-filings/enterprise/${this.selectedEnterpriseId}`
        }

        const response = await api.get(url)
        this.taxFilings = response.data || []
      } catch (error) {
        console.error('加载税务报表失败:', error)
      } finally {
        this.filingsLoading = false
      }
    },
    async loadMyApplications() {
      try {
        this.loading = true
        const response = await api.get('/enterprise-management/tax-applications')
        this.applications = response.data || []
      } catch (error) {
        console.error('加载申请列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    getStatusType(status) {
      const typeMap = {
        'PENDING': 'warning',
        'PROCESSING': 'info',
        'COMPLETED': 'success',
        'REJECTED': 'danger'
      }
      return typeMap[status] || 'info'
    },
    getFilingStatusType(status) {
      const typeMap = {
        '已申报': 'success',
        '待申报': 'warning',
        '逾期': 'danger',
        '已缴纳': 'success'
      }
      return typeMap[status] || 'info'
    },
    getApplicationTypeLabel(type) {
      const labelMap = {
        'TAX_REGISTRATION': '税务登记',
        'TAX_DECLARATION': '税务申报',
        'TAX_CHANGE': '税务变更',
        'TAX_CANCELLATION': '税务注销',
        'INVOICE_APPLICATION': '发票申请',
        'TAX_CONSULTATION': '税务咨询'
      }
      return labelMap[type] || type
    },
    getStatusLabel(status) {
      const labelMap = {
        'PENDING': '待处理',
        'PROCESSING': '处理中',
        'COMPLETED': '已完成',
        'REJECTED': '已拒绝',
        'CANCELLED': '已取消'
      }
      return labelMap[status] || status
    }
  }
}
</script>

<style scoped lang="scss">
.tax-agency-page {
  min-height: calc(100vh - 216px);
  background: #f5f5f5;
  padding: 40px 0;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-title {
  font-size: 32px;
  text-align: center;
  margin-bottom: 40px;
  color: #333;
}

.filings-card {
  margin-bottom: 30px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .empty-tip {
    padding: 40px 0;
  }
}

.form-card {
  margin-bottom: 30px;
}

.login-prompt-card {
  margin-bottom: 30px;

  .login-prompt {
    text-align: center;
    padding: 40px 20px;

    i {
      font-size: 48px;
      color: #E6A23C;
      margin-bottom: 20px;
    }

    p {
      font-size: 18px;
      color: #666;

      a {
        color: #667eea;
        text-decoration: none;
        font-weight: bold;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.applications-card {
  .section-title {
    font-size: 24px;
    margin-bottom: 20px;
    color: #333;
  }
}

.section-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

@media screen and (max-width: 768px) {
  .tax-agency-page {
    padding: 20px 0;
  }

  .container {
    padding: 0 15px;
  }

  .page-title {
    font-size: 24px;
    margin-bottom: 30px;
  }

  .form-card {
    ::v-deep .el-form {
      .el-form-item__label {
        width: 100px !important;
        font-size: 14px;
      }

      .el-form-item__content {
        margin-left: 100px !important;
      }
    }
  }

  .applications-card {
    .section-title {
      font-size: 20px;
    }

    ::v-deep .el-table {
      font-size: 12px;

      .el-table__header th,
      .el-table__body td {
        padding: 8px 5px;
      }
    }
  }
}
</style>
